/**
 * The lightbox's zoom and pan arithmetic (日本-350).
 *
 * Pure, and separated from the component for the reason `libraryDrag.ts` and `menuPlacement.ts`
 * already establish on this codebase: the interesting bugs on an interaction like this live in the
 * maths, not in the markup, and `npm test` has no DOM to reach the markup with anyway.
 *
 * ## The model
 *
 * The image is drawn centred in its container, then translated by `(x, y)` and scaled about that
 * centre. So a point `p` in image coordinates — origin at the image's own centre — lands at
 * `containerCentre + (x, y) + p × scale`. Everything below is that one sentence rearranged.
 */

export interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

/** How far past fit a pinch or a wheel may go, in either direction. */
const MIN_FACTOR = 0.5;
const MAX_FACTOR = 8;

/**
 * The scale at which the image just fits its container.
 *
 * **This is allowed to be greater than 1, and that is the point.** Most card images are smaller
 * than the viewport they open into, so fit scales them *up* — which is why the lightbox opens here
 * rather than at 100%, and why `原寸` is usually a way to make the image **smaller**. The toolbar is
 * honest about that: it shows the real percentage rather than pretending fit is 100%.
 */
export function fitScale(natural: Size, viewport: Size, padding = 0): number {
  const width = Math.max(1, viewport.width - padding * 2);
  const height = Math.max(1, viewport.height - padding * 2);
  if (natural.width <= 0 || natural.height <= 0) return 1;
  return Math.min(width / natural.width, height / natural.height);
}

/**
 * Bound a scale.
 *
 * The bounds hang off **both** fit and 1, so neither a huge screenshot nor a tiny icon can end up
 * with a range that excludes the other landmark: `原寸` must always be reachable, and so must fit.
 */
export function clampScale(scale: number, fit: number): number {
  const low = Math.min(fit, 1) * MIN_FACTOR;
  const high = Math.max(fit, 1) * MAX_FACTOR;
  return Math.min(high, Math.max(low, scale));
}

/**
 * Zoom by `factor`, keeping whatever is under `pointer` under `pointer`.
 *
 * `pointer` is relative to the **centre** of the container, which is what the model above is written
 * in. Zooming about the centre instead is the version everybody writes first, and it is wrong in a
 * way that is obvious the moment you use it: the detail you were reaching for slides away as you
 * zoom towards it.
 */
export function zoomAbout(
  state: ZoomState,
  pointer: { x: number; y: number },
  factor: number,
  fit: number,
): ZoomState {
  const scale = clampScale(state.scale * factor, fit);
  // The clamp may have refused some of the requested factor, so the ratio is recomputed from the
  // scale we actually landed on. Using `factor` here instead makes the image drift sideways every
  // time somebody keeps scrolling at the limit.
  const applied = scale / state.scale;
  return {
    scale,
    x: pointer.x - (pointer.x - state.x) * applied,
    y: pointer.y - (pointer.y - state.y) * applied,
  };
}

/**
 * Keep the image where it can be seen.
 *
 * On an axis where the scaled image is **smaller** than the container it is pinned to the centre —
 * there is nothing to pan to, and letting it wander leaves the picture floating against a corner.
 * On an axis where it is larger, panning is bounded so an edge can reach the container's edge and
 * no further: gutters on both sides of a zoomed-in image read as the image having been lost.
 */
export function clampPan(state: ZoomState, natural: Size, viewport: Size): ZoomState {
  const scaled = { width: natural.width * state.scale, height: natural.height * state.scale };
  const slackX = Math.max(0, (scaled.width - viewport.width) / 2);
  const slackY = Math.max(0, (scaled.height - viewport.height) / 2);
  return {
    scale: state.scale,
    x: Math.min(slackX, Math.max(-slackX, state.x)),
    y: Math.min(slackY, Math.max(-slackY, state.y)),
  };
}

/** Whether the image overflows its container on either axis — i.e. whether a drag has anywhere to go. */
export function canPan(state: ZoomState, natural: Size, viewport: Size): boolean {
  return (
    natural.width * state.scale > viewport.width + 1 ||
    natural.height * state.scale > viewport.height + 1
  );
}

const LINE_HEIGHT_PX = 16;
const PAGE_HEIGHT_PX = 400;

/**
 * A wheel notch to a zoom factor.
 *
 * **`deltaMode` is not optional to read.** Firefox reports lines and a page-scrolling device reports
 * pages, so a handler that treats `deltaY` as pixels everywhere zooms about sixteen times too
 * slowly on one browser and absurdly fast on another — and it is the kind of thing that is only
 * ever noticed by whoever is not using the browser it was written on.
 *
 * Exponential rather than linear so that zooming in and back out by the same number of notches
 * returns exactly where it started.
 */
export function wheelFactor(deltaY: number, deltaMode = 0): number {
  const pixels =
    deltaMode === 1 ? deltaY * LINE_HEIGHT_PX : deltaMode === 2 ? deltaY * PAGE_HEIGHT_PX : deltaY;
  return Math.exp(-pixels * 0.0015);
}

/** Two touch points to a zoom factor and the midpoint to zoom about. */
export function pinch(
  before: [{ x: number; y: number }, { x: number; y: number }],
  after: [{ x: number; y: number }, { x: number; y: number }],
): { factor: number; midpoint: { x: number; y: number } } {
  const spread = (p: [{ x: number; y: number }, { x: number; y: number }]) =>
    Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
  const from = spread(before);
  const to = spread(after);
  return {
    // A zero spread is two fingers reported at one point — a real event on some digitisers, and
    // dividing by it sends the scale to Infinity and the image off screen for the rest of the
    // session.
    factor: from <= 0 ? 1 : to / from,
    midpoint: { x: (after[0].x + after[1].x) / 2, y: (after[0].y + after[1].y) / 2 },
  };
}

/** `100%`. The percentage the toolbar prints, which is the **real** one rather than one relative to fit. */
export function zoomLabel(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

/**
 * What a double-click does: fit ⇄ 原寸.
 *
 * Compared with a tolerance because a wheel leaves the scale on an irrational-looking number, and
 * an equality test would make the gesture do nothing at all for the rest of the session.
 */
export function toggleScale(scale: number, fit: number): number {
  return Math.abs(scale - fit) < 0.01 ? 1 : fit;
}
